import {checkStockColor} from '@utils/color.utils'
import getUrl from '@utils/url-utils'

import {baseApi} from './base.slice'

const MACHINE_ENDPOINT = 'machines'
const CERTIFICATES_ENDPOINT = 'certificates'
const BASE_MACHINE_URL = new URL(
	`${process.env.GATSBY_BACKOFFICE_BASE_URL}/${MACHINE_ENDPOINT}`
).toString()

const BASE_CERTIFICATE_URL = new URL(
	`${process.env.GATSBY_BACKOFFICE_BASE_URL}/${CERTIFICATES_ENDPOINT}`
).toString()

const STOCK = 'stock'

export const machineApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getMachines: builder.query<
			GetMachinesResponse,
			{
				tenantId: string
				offset: number
				limit: number
				filter?: {property: string; value: string}
				sort?: {direction: 'asc' | 'desc'; property: string}
				activeMachinesOnly?: boolean
			}
		>({
			query: ({tenantId, offset, limit, sort, filter, activeMachinesOnly}) => {
				const url = getUrl(BASE_MACHINE_URL, {
					queryParams: new Map([
						['limit', limit.toString()],
						['offset', offset.toString()],
						['orderBy', sort ? `${sort.property}.${sort.direction}` : ''],
						['tenantId', tenantId],
						['isActive', activeMachinesOnly ? `1` : `0`],
					]),
				})

				if (filter?.property && filter?.value) {
					url.searchParams.set(
						`machine.${filter?.property}[like]`,
						filter?.value
					)
				}

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
			transformResponse: (response: GetMachinesResponse) => {
				const machineWithDerivedProperty = response.entries.map((machine) => {
					const {actualStock, minStock} = machine

					return {
						...machine,
						stockColor: checkStockColor(actualStock, minStock),
					}
				})

				return {
					entries: [...machineWithDerivedProperty],
					totalEntries: response.totalEntries,
				}
			},
			providesTags: ['Machines'],
		}),
		getMachine: builder.query<MachineDetail, string>({
			query: (machineId) => {
				return {
					input: `${BASE_MACHINE_URL}/${machineId}`,
					init: {method: 'GET'},
				}
			},
			providesTags: ['Machines'],
		}),
		getCertificates: builder.query<MachineCertificate[], void>({
			query: () => {
				return {
					input: BASE_CERTIFICATE_URL,
					init: {
						method: 'GET',
					},
				}
			},
			providesTags: ['Certificates'],
		}),
		addMachine: builder.mutation<MachineDetail, Partial<MachineDetail>>({
			query: (machine) => {
				// Delete these values, to prevent a 400 Bad Request
				delete machine.id
				delete machine.serialNumber
				delete machine.isActive
				delete machine.isInMaintenance

				return {
					input: BASE_MACHINE_URL,
					init: {method: 'POST', body: JSON.stringify(machine)},
				}
			},
			invalidatesTags: ['Machines', 'Certificates'],
		}),
		updateMachine: builder.mutation<
			MachineDetail,
			{machineId: string; machineDetails: Partial<MachineDetail>}
		>({
			query: ({machineId, machineDetails}) => {
				// Delete these values, to prevent a 400 Bad Request
				delete machineDetails.id
				delete machineDetails.tenantId
				delete machineDetails.gameTypeId
				delete machineDetails.serialNumber

				return {
					input: `${BASE_MACHINE_URL}/${machineId}`,
					init: {
						method: 'PUT',
						body: JSON.stringify(machineDetails),
					},
				}
			},
			invalidatesTags: ['Machines'],
		}),
		resetActualStock: builder.mutation<MachineDetail, {machineId: string}>({
			query: ({machineId}) => {
				return {
					input: `${BASE_MACHINE_URL}/${machineId}/${STOCK}`,
					init: {
						method: 'PUT',
						body: JSON.stringify(''),
					},
				}
			},
			invalidatesTags: ['Machines'],
		}),
		getMqttCredentials: builder.query<MachineMqttCredentials, string>({
			query: (machineId) => {
				return {
					input: `${BASE_MACHINE_URL}/${machineId}/credentials`,
					init: {method: 'GET'},
				}
			},
			transformResponse: (response: any): MachineMqttCredentials => {
				return {
					accessKey: response.AccessKeyId,
					secretKey: response.SecretAccessKey,
					sessionToken: response.SessionToken,
				}
			},
		}),
		getMachineCategories: builder.query<
			{
				entries: MachineCategory[]
				totalEntries: number
			},
			{
				tenantId?: string
				limit: number
				offset: number
			}
		>({
			query: ({tenantId, limit, offset}) => {
				const url = new URL(`${BASE_MACHINE_URL}/categories`)

				url.searchParams.set('limit', limit.toString())
				url.searchParams.set('offset', offset.toString())

				if (tenantId && tenantId !== 'all tenants') {
					url.searchParams.set('tenantId', tenantId)
				}

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
			providesTags: ['MachineCategories'],
		}),
		getMachineCategory: builder.query<MachineCategory, string>({
			query: (categoryId) => {
				return {
					input: `${BASE_MACHINE_URL}/categories/${categoryId}`,
					init: {method: 'GET'},
				}
			},
			providesTags: (result) => [
				{
					type: 'MachineCategory',
					id: result.id,
				},
			],
		}),
		addMachineCategory: builder.mutation<
			MachineCategory,
			Omit<MachineCategory, 'id' | 'createdAt' | 'updatedAt' | 'isActive'>
		>({
			query: (category) => {
				const url = new URL(`${BASE_MACHINE_URL}/categories`)

				return {
					input: url.toString(),
					init: {method: 'POST', body: JSON.stringify(category)},
				}
			},
			async onQueryStarted(patch, {dispatch, queryFulfilled}) {
				try {
					const {data: createdCategory} = await queryFulfilled
					dispatch(
						machineApi.util.upsertQueryData(
							'getMachineCategory',
							createdCategory.id,
							createdCategory
						)
					)
				} catch {}
			},
			invalidatesTags: ['MachineCategories', 'TenantMachineCategories'],
		}),
		updateMachineCategory: builder.mutation<
			MachineCategory,
			Omit<MachineCategory, 'createdAt' | 'updatedAt'>
		>({
			query: (category) => {
				return {
					input: `${BASE_MACHINE_URL}/categories`,
					init: {method: 'PUT', body: JSON.stringify(category)},
				}
			},
			async onQueryStarted({id, ...patch}, {dispatch, queryFulfilled}) {
				const patchResult = dispatch(
					machineApi.util.updateQueryData('getMachineCategory', id, (draft) => {
						Object.assign(draft, patch)
					})
				)

				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
				}
			},
			invalidatesTags: ['MachineCategories'],
		}),
		removeMachineCategory: builder.mutation<void, string>({
			query: (categoryId) => {
				return {
					input: `${BASE_MACHINE_URL}/categories/${categoryId}`,
					init: {method: 'DELETE'},
				}
			},
			invalidatesTags: () => ['MachineCategories', 'TenantMachineCategories'],
		}),
		getMachineCategoriesForTenant: builder.query<
			{
				entries: MachineCategory[]
				totalEntries: number
			},
			string
		>({
			query: (tenantId) => {
				const url = new URL(`${BASE_MACHINE_URL}/categories`)

				// url.searchParams.set('isActive', '1')
				url.searchParams.set('tenantId', tenantId)

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
			providesTags: ['TenantMachineCategories'],
		}),
	}),
})

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints.
 */
export const {
	useLazyGetMachinesQuery,
	useGetMachinesQuery,
	useLazyGetMachineQuery,
	useGetMachineQuery,
	useGetCertificatesQuery,
	useUpdateMachineMutation,
	useAddMachineMutation,
	useResetActualStockMutation,
	useLazyGetMqttCredentialsQuery,
	useLazyGetMachineCategoriesQuery,
	useLazyGetMachineCategoryQuery,
	useAddMachineCategoryMutation,
	useUpdateMachineCategoryMutation,
	useGetMachineCategoriesQuery,
	useGetMachineCategoryQuery,
	useRemoveMachineCategoryMutation,
	useLazyGetMachineCategoriesForTenantQuery,
} = machineApi
export default machineApi.reducer
